/**
 * Completitud de perfil tal y como la sirve la API (issue #1774). El porcentaje y las secciones pendientes se
 * calculan en el backend; el frontend solo los muestra.
 */
export enum ProfileCompletionStatus {
  Incomplete = 'Incomplete',
  Almost = 'Almost',
  Complete = 'Complete'
}

/**
 * Los cuatro campos que hay que pedir a OData para que la completitud llegue al navegador.
 *
 * Vive aqui, en una sola lista, porque OData solo devuelve lo que se le pide: un `select` explicito sustituye
 * al de por defecto del servicio, no se fusiona con el. Cuando la lista estaba escrita en cada sitio, los dos
 * layouts --que son los que cargan el perfil para toda la app-- se quedaron sin estos campos y todo consumidor
 * leia `undefined`, lo que ademas apagaba las condiciones de crear solicitud, subir documentacion y crear
 * bloque de disponibilidad. Anadir un campo tiene que ser un cambio en un unico sitio.
 */
export const COMPLETION_SELECT = ['completionPercentage', 'completionStatus', 'completionMissingRequired', 'completionMissingOptional'] as const;

export type ProfileCompletionField = (typeof COMPLETION_SELECT)[number];

/**
 * Opcionales a proposito: OData solo devuelve lo que se le pide, asi que un `select` que no los incluya deja
 * estos campos sin llegar. Declararlos obligatorios era lo que impedia a TypeScript avisar cuando los dos
 * layouts dejaron de pedirlos.
 */
export interface ProfileCompletionFields {
  completionPercentage?: number;
  completionStatus?: ProfileCompletionStatus;
  /**
   * Las claves de seccion vienen como JSON (`["MIN_PERSONAL", ...]`), no como array: OData infiere cualquier
   * coleccion del EDM como una coleccion real y al resolver `$select` sobre ella intenta traducir un proyector
   * por elemento que no existe en SQL para una columna de texto -- devolvia un 500. Se parsean con
   * `parseSections`.
   */
  completionMissingRequired?: string;
  completionMissingOptional?: string;
}

/** Las claves de seccion viajan como JSON en un string: ver `ProfileCompletionFields`. */
export const parseSections = (json: string | undefined): string[] => {
  if (!json) return [];

  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Extrae solo los campos de completitud de una respuesta, para mezclarlos sobre el perfil que ya esta en
 * memoria sin arrastrar el resto. Las pantallas actualizan el perfil campo a campo sin volver a pedirlo, y el
 * porcentaje --que ahora es un valor de servidor y no algo derivado de esos campos-- hay que refrescarlo
 * aparte o se queda congelado hasta recargar la pagina.
 *
 * Solo se copian las claves que vienen de verdad. Rellenar las que falten con 0 / `Incomplete` seria inventar:
 * esta funcion mezcla, y un 0 inventado apaga las condiciones de crear solicitud, subir documentacion y crear
 * bloque de disponibilidad, que miran `completionPercentage > 0`. Si el campo no llega, se conserva el que ya
 * hubiese.
 */
export const pickCompletion = (source: Partial<ProfileCompletionFields> | undefined): Partial<ProfileCompletionFields> => {
  const picked: Partial<ProfileCompletionFields> = {};

  COMPLETION_SELECT.forEach((field) => {
    // `!= null` y no `!== undefined`: un `null` tambien tiene que descartarse, o sobreescribiria un valor bueno
    // con algo que despues se lee como 0 / Incomplete, que es justo lo que esta funcion evita.
    if (source?.[field] != null) (picked as Record<string, unknown>)[field] = source[field];
  });

  return picked;
};
